export default (configuration = {}) => ({
  bounds: null,
  imageWidth: 0,
  imageHeight: 0,
  pageWidthMM: configuration.pageWidthMM,
  pageHeightMM: configuration.pageHeightMM,
  minimumPrintableWidthMM: configuration.minimumPrintableWidthMM,
  minimumPrintableHeightMM: configuration.minimumPrintableHeightMM,
  pixelsPerMMX: 0,
  pixelsPerMMY: 0,
  dragging: false,
  resizing: false,
  resizeDirection: null,
  printableStyle: '',

  init(bounds) {
    this.bounds = bounds ?? {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    }

    this.calculateSizes()

    this._mousemoveHandler = this.onDrag.bind(this)
    this._mouseupHandler = () => {
      this.dragging = false
      this.resizing = false
    }
    window.addEventListener('mousemove', this._mousemoveHandler)
    window.addEventListener('mouseup', this._mouseupHandler)
  },

  destroy() {
    if (this._mousemoveHandler) {
      window.removeEventListener('mousemove', this._mousemoveHandler)
    }
    if (this._mouseupHandler) {
      window.removeEventListener('mouseup', this._mouseupHandler)
    }
  },

  updateEditor(event) {
    this.bounds = event.detail.bounds
    this.updateBox()
  },

  calculateSizes() {
    const rect = this.$refs.image.getBoundingClientRect()
    this.imageWidth = rect.width
    this.imageHeight = rect.height

    this.pixelsPerMMX = this.imageWidth / this.pageWidthMM
    this.pixelsPerMMY = this.imageHeight / this.pageHeightMM

    this.updateBox()
  },

  updateBox() {
    const left = this.boundValue('left') * this.pixelsPerMMX
    const top = this.boundValue('top') * this.pixelsPerMMY
    const right = this.imageWidth - (this.boundValue('right') * this.pixelsPerMMX)
    const bottom = this.imageHeight - (this.boundValue('bottom') * this.pixelsPerMMY)

    const width = right - left
    const height = bottom - top

    this.printableStyle = `top: ${top}px; left: ${left}px; width: ${width}px; height: ${height}px;`

    if (this.$refs.xMarker) this.$refs.xMarker.style.left = `${left}px`
    if (this.$refs.yMarker) this.$refs.yMarker.style.top = `${top}px`

    const rightPx = this.pageWidthMM * this.pixelsPerMMX - this.boundValue('right') * this.pixelsPerMMX - 1
    const bottomPx = this.pageHeightMM * this.pixelsPerMMY - this.boundValue('bottom') * this.pixelsPerMMY - 1
    if (this.$refs.xMarker2) this.$refs.xMarker2.style.left = `${rightPx}px`
    if (this.$refs.yMarker2) this.$refs.yMarker2.style.top = `${bottomPx}px`
  },

  boundValue(side) {
    const value = Number(this.bounds?.[side])

    return Number.isFinite(value) ? value : 0
  },

  maximumFor(side) {
    const geometry = {
      top: ['bottom', this.pageHeightMM, this.minimumPrintableHeightMM],
      right: ['left', this.pageWidthMM, this.minimumPrintableWidthMM],
      bottom: ['top', this.pageHeightMM, this.minimumPrintableHeightMM],
      left: ['right', this.pageWidthMM, this.minimumPrintableWidthMM]
    }
    const [opposite, pageSize, minimumPrintableSize] = geometry[side] ?? [null, 0, 0]

    return Math.max(0, pageSize - minimumPrintableSize - this.boundValue(opposite))
  },

  updateFromInput(side) {
    const rawValue = this.bounds?.[side]

    // Keep an empty input empty so Livewire can return an actionable validation
    // error. Numeric values are constrained to the same A4 geometry as the server.
    if (rawValue !== '' && rawValue !== null && rawValue !== undefined) {
      const value = Number(rawValue)

      if (Number.isFinite(value)) {
        this.bounds[side] = Math.round(Math.min(Math.max(0, value), this.maximumFor(side)))
      }
    }

    this.updateBox()
  },

  hasUsableScale() {
    return Number.isFinite(this.pixelsPerMMX) && this.pixelsPerMMX > 0 &&
      Number.isFinite(this.pixelsPerMMY) && this.pixelsPerMMY > 0
  },

  startDrag(e) {
    e.preventDefault()
    this.dragging = true
    this.offsetX = e.offsetX
    this.offsetY = e.offsetY
  },

  startResize(e, direction) {
    e.preventDefault()
    this.resizing = true
    this.resizeDirection = direction
  },

  onDrag(e) {
    if ((!this.dragging && !this.resizing) || !this.hasUsableScale()) return

    const container = this.$refs.printable.parentElement.getBoundingClientRect()

    if (this.dragging) {
      let x = e.clientX - container.left - this.offsetX
      let y = e.clientY - container.top - this.offsetY

      const widthMM = this.pageWidthMM - this.boundValue('left') - this.boundValue('right')
      const heightMM = this.pageHeightMM - this.boundValue('top') - this.boundValue('bottom')
      const widthPx = widthMM * this.pixelsPerMMX
      const heightPx = heightMM * this.pixelsPerMMY

      const maxX = this.pageWidthMM * this.pixelsPerMMX - widthPx
      const maxY = this.pageHeightMM * this.pixelsPerMMY - heightPx

      x = Math.min(Math.max(0, x), maxX)
      y = Math.min(Math.max(0, y), maxY)

      this.bounds.left = Math.round(x / this.pixelsPerMMX)
      this.bounds.top = Math.round(y / this.pixelsPerMMY)
      this.bounds.right = this.pageWidthMM - this.bounds.left - widthMM
      this.bounds.bottom = this.pageHeightMM - this.bounds.top - heightMM
    }

    if (this.resizing) {
      const pos = {
        x: e.clientX - container.left,
        y: e.clientY - container.top
      }
      const right = this.pageWidthMM * this.pixelsPerMMX
      const bottom = this.pageHeightMM * this.pixelsPerMMY

      switch (this.resizeDirection) {
        case 'top':
          this.bounds.top = Math.round(Math.max(0, pos.y / this.pixelsPerMMY))
          break
        case 'bottom':
          this.bounds.bottom = Math.round(Math.max(0, (bottom - pos.y) / this.pixelsPerMMY))
          break
        case 'left':
          this.bounds.left = Math.round(Math.max(0, pos.x / this.pixelsPerMMX))
          break
        case 'right':
          this.bounds.right = Math.round(Math.max(0, (right - pos.x) / this.pixelsPerMMX))
          break
        case 'top-left':
          this.bounds.top = Math.round(Math.max(0, pos.y / this.pixelsPerMMY))
          this.bounds.left = Math.round(Math.max(0, pos.x / this.pixelsPerMMX))
          break
        case 'top-right':
          this.bounds.top = Math.round(Math.max(0, pos.y / this.pixelsPerMMY))
          this.bounds.right = Math.round(Math.max(0, (right - pos.x) / this.pixelsPerMMX))
          break
        case 'bottom-left':
          this.bounds.bottom = Math.round(Math.max(0, (bottom - pos.y) / this.pixelsPerMMY))
          this.bounds.left = Math.round(Math.max(0, pos.x / this.pixelsPerMMX))
          break
        case 'bottom-right':
          this.bounds.bottom = Math.round(Math.max(0, (bottom - pos.y) / this.pixelsPerMMY))
          this.bounds.right = Math.round(Math.max(0, (right - pos.x) / this.pixelsPerMMX))
          break
      }

      // Enforce minimum width/height in mm
      const currentWidth = this.pageWidthMM - this.bounds.left - this.bounds.right
      const currentHeight = this.pageHeightMM - this.bounds.top - this.bounds.bottom

      if (currentWidth < this.minimumPrintableWidthMM) {
        if (this.resizeDirection.includes('left')) {
          this.bounds.left = this.pageWidthMM - this.bounds.right - this.minimumPrintableWidthMM
        } else if (this.resizeDirection.includes('right')) {
          this.bounds.right = this.pageWidthMM - this.bounds.left - this.minimumPrintableWidthMM
        }
      }

      if (currentHeight < this.minimumPrintableHeightMM) {
        if (this.resizeDirection.includes('top')) {
          this.bounds.top = this.pageHeightMM - this.bounds.bottom - this.minimumPrintableHeightMM
        } else if (this.resizeDirection.includes('bottom')) {
          this.bounds.bottom = this.pageHeightMM - this.bounds.top - this.minimumPrintableHeightMM
        }
      }
    }

    this.updateBox()
  },

  get horizontalTicks() {
    const tickEveryMM = 10
    const tickWidthPx = this.pixelsPerMMX * tickEveryMM
    const count = Math.floor(this.pageWidthMM / tickEveryMM)
    return Array.from({ length: count }, (_, i) => ({ label: i * tickEveryMM, width: tickWidthPx }))
  },

  get verticalTicks() {
    const tickEveryMM = 10
    const tickHeightPx = this.pixelsPerMMY * tickEveryMM
    const count = Math.floor(this.pageHeightMM / tickEveryMM)
    return Array.from({ length: count }, (_, i) => ({ label: i * tickEveryMM, height: tickHeightPx }))
  }
})
